import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { InstantSearch } from 'react-instantsearch';
import store, {
  addFilters,
  setColumns,
  setFiltersVisibilityMap,
  setIndexes,
  setSearchClientConfig,
  setVisibleTables
} from './grouped-table/store';
import { buildAdapter, requestErrorHandler } from './grouped-table/helper';
import ComponentPreview from './grouped-table/partials/ComponentPreview';
import Group from './grouped-table/partials/Group';
import FiltersBlock from './grouped-table/partials/FiltersBlock';

const futureFlags = () => {
  return {
    preserveSharedStateOnUnmount: true,
  };
};

const normalizeFilters = (filters = [], columns) => {
  return filters.map(({attribute}) => {
    return {
      attribute,
      widget: 'checkboxes',
      fieldName: attribute,
      label: columns[attribute],
    };
  });
};

const GroupedTableComponent = (props) => {
  const {configUrl, tableStyle, options} = props;
  const dispatch = useDispatch();
  const containerRef = useRef();
  const [defaultIndex, setDefaultIndex] = useState('');

  const columns = useSelector(state => state.app.columns);
  const indexes = useSelector(state => state.app.indexes);
  const typesenseClientConfig = useSelector(state => state.app.searchClientConfig);
  const filtersVisibilityMap = useSelector(state => state.app.filtersVisibilityMap);
  const visibleTables = useSelector(state => state.app.visibleTables);
  const groups = Object.keys(indexes);

  useEffect(() => {
    fetch(configUrl)
      .then(response => response.json())
      .then(response => {
        const {
          columns,
          groups,
          defaultIndex,
          typesenseClientConfig,
          filters_visibility_map = [],
          filters,
        } = response;

        dispatch(setColumns(columns));
        dispatch(setIndexes(groups));
        dispatch(setSearchClientConfig(typesenseClientConfig));
        dispatch(setFiltersVisibilityMap(filters_visibility_map));
        dispatch(addFilters(normalizeFilters(filters || [], columns)));
        setDefaultIndex(defaultIndex);
      }).catch(requestErrorHandler)
  }, [configUrl]);

  const adapter = useMemo(() => buildAdapter(defaultIndex, typesenseClientConfig), [defaultIndex]);

  if (!defaultIndex || !groups.length) {
    return <ComponentPreview/>;
  }

  return (
    <div className='nv-grouped-table' ref={containerRef}>
      <InstantSearch searchClient={adapter.searchClient} indexName={defaultIndex} future={futureFlags()}>
        <FiltersBlock containerRef={containerRef} filtersVisibilityMap={filtersVisibilityMap} visibleTablesSetter={setVisibleTables} options={options}/>
        {groups.map((group) => {
          if (visibleTables?.length > 0 && !visibleTables.includes(group)) {
            return null;
          }
          const key = `group:${group}`;
          return <Group key={key} indexName={defaultIndex} group={group} columns={columns} indexes={indexes} tableStyle={tableStyle}/>;
        })}
      </InstantSearch>
    </div>
  );
};

/**
 *
 * @param props
 * @returns {JSX.Element}
 */
export default function (props) {
  const {config_url: configUrl, table_style: tableStyle, hide_search_input: hideSearchInput = false} = props;

  if (!configUrl) {
    return <ComponentPreview/>;
  }

  return (
    <Provider store={store}>
      <GroupedTableComponent configUrl={configUrl} tableStyle={tableStyle} options={{hideSearchInput}}/>
    </Provider>
  );
}
